// src/Components/Modules/HR/LeaveApproval.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Badge as MuiBadge,
  MenuItem,
} from "@mui/material";
import {
  FaCheck,
  FaTimes,
  FaClock,
  FaUserClock,
  FaCalendarAlt,
  FaCalendarDay,
  FaCalendarCheck,
  FaCalendarTimes,
  FaSearch,
  FaSync,
  FaFilter,
  FaUser,
  FaInfoCircle,
  FaEye,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme, status }) => {
  const colors = {
    pending: { bg: "#fff3e0", color: "#ef6c00", icon: "#ff9800" },
    approved: { bg: "#e8f5e9", color: "#2e7d32", icon: "#4caf50" },
    rejected: { bg: "#ffebee", color: "#c62828", icon: "#f44336" },
    total: { bg: "#e3f2fd", color: "#1565c0", icon: "#2196f3" },
  };

  const palette = colors[status] || colors.total;

  return {
    background: palette.bg,
    borderRadius: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.8, 2),
  minWidth: "auto",
  ...(variant === "approve" && {
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#1b5e20",
    "&:hover": {
      background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
      boxShadow: "0 4px 12px rgba(67, 233, 123, 0.3)",
    },
  }),
  ...(variant === "reject" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 4px 12px rgba(250, 112, 154, 0.3)",
    },
  }),
}));

const LeaveApproval = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewDialog, setViewDialog] = useState(null);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get("hr/leave/pending");
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Failed to load leave requests");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave requests. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    // ✅ If rejecting, ask for reason
    if (status === 'reject') {
      const { value: reason } = await Swal.fire({
        title: "Reject Leave Request",
        input: 'textarea',
        inputLabel: 'Rejection Reason *',
        inputPlaceholder: 'Please provide a reason for rejection...',
        inputAttributes: {
          'aria-label': 'Rejection reason',
          'maxlength': 500
        },
        inputValidator: (value) => {
          if (!value || value.trim().length < 5) {
            return 'Please provide a valid rejection reason (min 5 characters)';
          }
        },
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        cancelButtonColor: "#757575",
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return 'You need to provide a rejection reason!';
          }
        }
      });

      if (!reason) return; // User cancelled

      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`hr/leave/update/${id}`, {
          status: 'reject',
          rejection_reason: reason,
        });
        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: "Leave request has been rejected.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchPending();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Action failed";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
      return;
    }

    // ✅ If approving, just confirm
    const result = await Swal.fire({
      title: "Approve Leave Request?",
      html: `
            <div style="text-align: left; padding: 10px;">
                <p>Are you sure you want to <strong>approve</strong> this leave request?</p>
                <p style="color: #757575; font-size: 14px;">This action cannot be undone.</p>
            </div>
        `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4caf50",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`hr/leave/update/${id}`, {
          status: 'approve',
        });
        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: "Leave request has been approved.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchPending();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Action failed";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => {
      const employeeName = item.employee?.employee_name?.toLowerCase() || "";
      const leaveType = item.leaveType?.name?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();

      const matchesSearch =
        employeeName.includes(searchTerm) ||
        leaveType.includes(searchTerm) ||
        status.includes(searchTerm);

      const matchesFilter =
        statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [data, search, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(data)) return { total: 0, pending: 0, approved: 0, rejected: 0 };
    return {
      total: data.length,
      pending: data.filter((d) => d.status === "pending").length,
      approved: data.filter((d) => d.status === "approved" || d.status === "approve").length,
      rejected: data.filter((d) => d.status === "rejected" || d.status === "reject").length,
    };
  }, [data]);

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "pending") {
      return (
        <Chip
          icon={<FaClock style={{ fontSize: 12 }} />}
          label="Pending"
          size="small"
          sx={{
            bgcolor: "#fff3e0",
            color: "#ef6c00",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#ff9800" },
          }}
        />
      );
    }
    if (statusLower === "approved" || statusLower === "approve") {
      return (
        <Chip
          icon={<FaCheck style={{ fontSize: 12 }} />}
          label="Approved"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    if (statusLower === "rejected" || statusLower === "reject") {
      return (
        <Chip
          icon={<FaTimes style={{ fontSize: 12 }} />}
          label="Rejected"
          size="small"
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#f44336" },
          }}
        />
      );
    }
    return (
      <Chip
        label={status || "N/A"}
        size="small"
        sx={{ bgcolor: "#f5f5f5", color: "#757575", fontWeight: 600 }}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return diff > 0 ? diff : 0;
    } catch {
      return 0;
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Leave Approvals" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Leave Approvals" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserClock style={{ marginRight: 10, verticalAlign: "middle" }} />
              Leave Approvals
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Review and manage employee leave requests
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchPending}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="total" elevation={0}>
            <CardContent>
              <FaCalendarAlt size={32} color="#2196f3" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#1565c0">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="#1565c0" fontWeight={600}>
                Total Requests
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="pending" elevation={0}>
            <CardContent>
              <FaClock size={32} color="#ff9800" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#ef6c00">
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="#ef6c00" fontWeight={600}>
                Pending
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="approved" elevation={0}>
            <CardContent>
              <FaCalendarCheck size={32} color="#4caf50" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#2e7d32">
                {stats.approved}
              </Typography>
              <Typography variant="body2" color="#2e7d32" fontWeight={600}>
                Approved
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="rejected" elevation={0}>
            <CardContent>
              <FaCalendarTimes size={32} color="#f44336" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#c62828">
                {stats.rejected}
              </Typography>
              <Typography variant="body2" color="#c62828" fontWeight={600}>
                Rejected
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by employee, leave type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell>Leave Type</StyledTableCell>
              <StyledTableCell>Duration</StyledTableCell>
              <StyledTableCell>Days</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Reason</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton width={120} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={150} /></TableCell>
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaUser style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No leave requests found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "No pending leave requests at the moment"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const employeeName = row.employee?.employee_name || "N/A";
                const days = row.days_count || calculateDays(row.start_date, row.end_date);
                const statusLower = row.status?.toLowerCase();
                const isPending = statusLower === "pending";

                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: getColorFromName(employeeName),
                            width: 40,
                            height: 40,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(employeeName)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {employeeName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.employee?.designation || "Employee"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.leaveType?.name || "N/A"}
                        size="small"
                        sx={{
                          bgcolor: "#e3f2fd",
                          color: "#1976d2",
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaCalendarAlt style={{ fontSize: 12, color: "#757575" }} />
                          <Typography variant="body2" noWrap>
                            {formatDate(row.start_date)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          to {formatDate(row.end_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${days} day${days > 1 ? "s" : ""}`}
                        size="small"
                        sx={{
                          bgcolor: "#f3e5f5",
                          color: "#7b1fa2",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={row.duration_type === "half_day" ? <FaClock /> : <FaCalendarAlt />}
                        label={row.duration_type === "half_day" ? "Half Day" : "Full Day"}
                        size="small"
                        sx={{
                          bgcolor: row.duration_type === "half_day" ? "#fff3e0" : "#e8f5e9",
                          color: row.duration_type === "half_day" ? "#ef6c00" : "#2e7d32",
                          fontWeight: 600,
                          "& .MuiChip-icon": {
                            color: row.duration_type === "half_day" ? "#ff9800" : "#4caf50"
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>
                      <Tooltip title={row.reason || "No reason provided"}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.reason || (
                            <span style={{ color: "#bdbdbd", fontStyle: "italic" }}>
                              No reason
                            </span>
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {isPending ? (
                          <>
                            <Tooltip title="Approve">
                              <span>
                                <ActionButton
                                  variant="approve"
                                  size="small"
                                  disabled={actionLoading === row.id}
                                  onClick={() => handleAction(row.id, "approve")}
                                  startIcon={actionLoading === row.id ? null : <FaCheck />}
                                >
                                  {actionLoading === row.id ? "Processing..." : "Approve"}
                                </ActionButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <span>
                                <ActionButton
                                  variant="reject"
                                  size="small"
                                  disabled={actionLoading === row.id}
                                  onClick={() => handleAction(row.id, "reject")}
                                  startIcon={actionLoading === row.id ? null : <FaTimes />}
                                >
                                  {actionLoading === row.id ? "Processing..." : "Reject"}
                                </ActionButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setViewDialog(row)}
                              sx={{ color: "#2196f3" }}
                            >
                              <FaEye />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>

      {/* View Details Dialog */}
      <Dialog
        open={!!viewDialog}
        onClose={() => setViewDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FaInfoCircle />
            Leave Request Details
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewDialog && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Employee
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewDialog.employee?.employee_name || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Leave Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewDialog.leaveType?.name || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDate(viewDialog.start_date)} to {formatDate(viewDialog.end_date)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Duration Type
                </Typography>
                <Chip
                  icon={viewDialog.duration_type === "half_day" ? <FaClock /> : <FaCalendarAlt />}
                  label={viewDialog.duration_type === "half_day" ? "Half Day (0.5 Day)" : "Full Day"}
                  size="small"
                  sx={{
                    bgcolor: viewDialog.duration_type === "half_day" ? "#fff3e0" : "#e8f5e9",
                    color: viewDialog.duration_type === "half_day" ? "#ef6c00" : "#2e7d32",
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Days
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewDialog.days_count || calculateDays(viewDialog.start_date, viewDialog.end_date)} day(s)
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                {getStatusChip(viewDialog.status)}
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body1">
                  {viewDialog.reason || "No reason provided"}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveApproval;